import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  isQrMarkKey,
  listCdnObjects,
  listCdnProfiles,
  objectFilename,
} from '../CdnAssets/api';
import type { CdnObject } from '../CdnAssets/types';
import { GO_QR_LOGO_SRC, type GoQrLogoChoice } from '../../lib/goQr';

export default function GoQrLogoPicker({
  current,
  onPick,
}: {
  current: GoQrLogoChoice;
  onPick: (choice: GoQrLogoChoice) => void;
}) {
  const [query, setQuery] = useState('');
  const [prefix, setPrefix] = useState('PNG/');
  const [objects, setObjects] = useState<CdnObject[]>([]);
  const [folders, setFolders] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const profiles = await listCdnProfiles();
        const profile = profiles[0];
        if (!profile) throw new Error('No CDN profile');
        const result = await listCdnObjects({ profileId: profile.id, prefix });
        if (cancelled) return;
        setObjects(result.objects.filter((obj) => isQrMarkKey(obj.key)));
        setFolders(result.prefixes);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'CDN list failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [prefix]);

  const q = query.trim().toLowerCase();
  const matches = useMemo(
    () =>
      objects.filter((obj) => {
        if (!q) return true;
        return obj.key.toLowerCase().includes(q) || objectFilename(obj.key).toLowerCase().includes(q);
      }),
    [objects, q],
  );

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-600 p-3 space-y-2">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onPick({ src: GO_QR_LOGO_SRC, label: 'Low Code CTO' })}
          className={`text-xs px-2 py-1 rounded-md border ${
            current.src === GO_QR_LOGO_SRC
              ? 'border-emerald-500 text-emerald-700 dark:text-emerald-300'
              : 'border-gray-300 dark:border-gray-600'
          }`}
        >
          Default mark
        </button>
        <label className="text-xs px-2 py-1 rounded-md border border-gray-300 dark:border-gray-600 cursor-pointer">
          From file
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = () => {
                if (typeof reader.result === 'string') {
                  onPick({ src: reader.result, label: file.name });
                }
              };
              reader.readAsDataURL(file);
              e.target.value = '';
            }}
          />
        </label>
        {prefix !== 'PNG/' ? (
          <button
            type="button"
            onClick={() => setPrefix('PNG/')}
            className="text-xs px-2 py-1 rounded-md border border-gray-300 dark:border-gray-600"
          >
            PNG/
          </button>
        ) : null}
      </div>
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search CDN keys (logo, friday…)"
        className="w-full px-2 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
      />
      {folders.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {folders.slice(0, 8).map((folder) => (
            <button
              key={folder}
              type="button"
              onClick={() => setPrefix(folder)}
              className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
            >
              {folder}
            </button>
          ))}
        </div>
      ) : null}
      {loading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="h-4 w-4 animate-spin text-emerald-500" />
        </div>
      ) : error ? (
        <p className="text-xs text-amber-700 dark:text-amber-300">{error}</p>
      ) : matches.length === 0 ? (
        <p className="text-xs text-gray-500">No images in {prefix || 'root'}.</p>
      ) : (
        <ul className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto">
          {matches.slice(0, 40).map((obj) => (
            <li key={obj.key}>
              <button
                type="button"
                onClick={() => onPick({ src: obj.publicUrl, label: objectFilename(obj.key) })}
                className={`w-full rounded-md border p-1 hover:border-emerald-500 ${
                  current.src === obj.publicUrl ? 'border-emerald-500' : 'border-gray-200 dark:border-gray-600'
                }`}
                title={obj.key}
              >
                <img
                  src={obj.publicUrl}
                  alt=""
                  crossOrigin="anonymous"
                  className="h-12 w-full object-contain bg-white"
                />
                <span className="block truncate text-[10px] text-gray-500 mt-0.5">
                  {objectFilename(obj.key)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
